import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom';
import styled from "@emotion/styled";
import { alpha, getOverlayAlpha } from '@mui/material/styles';
import { Tab, Tabs, Typography, Box, Divider, IconButton, CircularProgress, Tooltip, Button, Menu, MenuItem, Chip, LinearProgress, Dialog, DialogTitle, DialogContent, DialogActions, Table, TableHead, TableBody, TableRow, TableCell, useMediaQuery, TextField } from "@mui/material";
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import SortIcon from '@mui/icons-material/Sort';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import AccountTabProjects from './AccountTabProjects';
import AccountTabProjectsList from './AccountTabProjectsList';
import AccountTabCalendar from './AccountTabCalendar';
import AccountTabActivity from './AccountTabActivity';
import AccountTabPeople from './AccountTabPeople';
import CreateProjectDialog from './CreateProjectDialog';
import CreateGroupDialog from './CreateGroupDialog';
import { Add } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useHasPermission } from '../utils/permissionUtils';
import { useIsLocked } from '../utils/lockUtils';
import { addUpTime, convertMinutesToHours } from '../utils/timeUtils';
import { deleteGroup } from '../redux/ducks/group';
import { useDispatch, useSelector } from 'react-redux';
import { showAlert } from '../redux/slices/alerts';
import { triggerRefresh } from '../redux/slices/refreshTriggers';
import { setUserPreferences } from '../redux/slices/auth';
import { updateUser } from '../redux/slices/users';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import QuoteRequestDialog from '../components/QuoteRequestDialog';
import CompleteCascadeDialog from './CompleteCascadeDialog';
import BillingImpactDialog from './BillingImpactDialog';

const GridContainer = styled('div')(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: theme.spacing(2),
    padding: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(2),
    }
}));

const ProjectsTable = styled(Table)({
    tableLayout: 'fixed',
    width: '100%',
});

const NumericCol = styled('col')({ width: 75 });
const IconsCol = styled('col')({ width: 100 });

const ListViewToggle = styled(IconButton)(({ theme }) => ({
    display: 'none',
    [theme.breakpoints.up('md')]: {
        display: 'flex',
    }
}));

const PanelBox = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1, 0, 1),
    [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(0, 3),
    }
}));

const ViewBox = styled(Box)(({ theme }) => ({
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    padding: theme.spacing(0, 2),
}))

const StyledTabs = styled(Tabs)(({ theme }) => {
    const overlay = theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, getOverlayAlpha(1)) : 'transparent';
    const fade = (direction) => `linear-gradient(to ${direction}, ${overlay} 40%, transparent), linear-gradient(to ${direction}, ${theme.palette.background.paper} 40%, transparent)`;
    return {
        flex: 1,
        minWidth: 0,
        maxWidth: '100%',
        "& .MuiTabs-scrollButtons.Mui-disabled": {
            opacity: 0.3,
        },
        [theme.breakpoints.down('sm')]: {
            flexBasis: '100%',
            position: 'relative',
            "& .MuiTab-root": {
                minWidth: 'auto',
                padding: theme.spacing(1, 1.5),
                fontSize: theme.typography.size.md,
            },
            "& .MuiTabs-scrollButtons": {
                position: 'absolute',
                top: 0,
                bottom: 0,
                width: 28,
                zIndex: 1,
                opacity: 1,
            },
            "& .MuiTabs-scrollButtons:first-child": {
                left: 0,
                backgroundImage: fade('right'),
            },
            "& .MuiTabs-scrollButtons:last-child": {
                right: 0,
                backgroundImage: fade('left'),
            },
            "& .MuiTabs-scrollButtons.Mui-disabled": {
                opacity: 0,
                pointerEvents: 'none',
            },
        }
    };
})

const TabContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(0, 2),
    [theme.breakpoints.down('sm')]: {
        gap: theme.spacing(1),
    },
}))

const AddButtons = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
    marginLeft: 'auto',
    [theme.breakpoints.down('sm')]: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        width: '100%',
        gap: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        "& .MuiButton-root": {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: theme.spacing(5),
        },
    }
}))

const AddProjectButton = styled(Button)(({ theme }) => ({
    display: 'block',
}))

const AddGroupButton = styled(Button)(({ theme }) => ({
    display: 'block',
}))

const RequestQuoteButton = styled(AddGroupButton)(({ theme }) => ({
    [theme.breakpoints.down('sm')]: {
        gridColumn: '1 / -1',
        gridRow: 2,
    }
}))

const StyledGroupHeader = styled(Box, { shouldForwardProp: (prop) => prop !== 'clickable' })(({ theme, clickable }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    margin: theme.spacing(2, 0, 0, 0),
    padding: theme.spacing(0, 0, 0, 0),
    width: '100%',
    cursor: clickable ? 'pointer' : 'default',
    // borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        gap: theme.spacing(1),
        padding: theme.spacing(0, 1, 0, 1),
    }
}));

const GroupExpandIcon = styled(ExpandMoreIcon, { shouldForwardProp: (prop) => prop !== 'expanded' })(({ theme, expanded }) => ({
    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
    transition: 'transform 0.2s',
    color: theme.palette.text.secondary,
    flexShrink: 0,
}));

const GroupTitle = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary,
    fontSize: theme.typography.h5.fontSize,
    marginRight: 'auto',
    [theme.breakpoints.down('sm')]: {
        fontSize: theme.typography.h6.fontSize,
        marginBottom: theme.spacing(1),
    }
}));

const ProjectCount = styled(Chip)(({ theme }) => ({
    fontSize: theme.typography.body2.fontSize,
}));

const DeleteMenuItem = styled(MenuItem)(({ theme }) => ({
    color: theme.palette.error.main,
}));

const AddProjectToGroupButton = styled(IconButton)(({ theme }) => ({
    color: theme.palette.primary.main,
}));

const HeaderTitleBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    width: '100%',
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'flex-end',   
        justifyContent: 'flex-end',
        gap: theme.spacing(1),
    }
}));

const HeaderActionsBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
        alignItems: 'flex-end',   
        justifyContent: 'flex-end',
        gap: theme.spacing(1),
        width: '100%',
    }
}));

const PercentageBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
        alignItems: 'center',   
        justifyContent: 'center',
        gap: theme.spacing(1),
        width: '100%',
    }
}));

const PercentageBar = styled(Box)(({ theme }) => ({
    width: '100%',
    marginRight: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
        width: '100%',
        marginLeft: theme.spacing(1),
    }
}));

const PercentageText = styled(Box)(({ theme }) => ({
    minWidth: 35,
    [theme.breakpoints.down('sm')]: {
        minWidth: 35,
    }
}));

const UngroupedDivider = styled(Divider)(({ theme }) => ({
    marginTop: theme.spacing(1),
}));

const FilterBox = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1, 0, 2, 0),
}));

const LoadMoreBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(1, 0),
}));

const SortMenuItem = styled(MenuItem)(({ theme }) => ({
    minWidth: 200,
    justifyContent: 'space-between',
    gap: theme.spacing(2),
}));

const SortDirectionIcon = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    width: 20,
});

const projectNumberValue = (project) => {
    const value = project?.projectId === null || project?.projectId === undefined ? NaN : Number(project.projectId);
    return Number.isFinite(value) ? value : Infinity;
};

const PROJECT_SORTS = [
    { field: 'number', label: 'Project Number', defaultDirection: 'asc' },
    { field: 'name', label: 'Alphabetical', defaultDirection: 'asc' },
    { field: 'due', label: 'Due Date', defaultDirection: 'asc' },
    { field: 'start', label: 'Start Date', defaultDirection: 'desc' },
    { field: 'completed', label: 'Completed Date', defaultDirection: 'desc', completedOnly: true },
];

const sortKeyOf = (field, direction) => (direction === 'desc' ? `${field}-desc` : field);

const SORT_DATE_FIELDS = { due: 'dueDate', start: 'startDate', completed: 'completedDate' };

const effectiveSortFor = (sort, isCompleted) => {
    const [field] = String(sort || '').split('-');
    const entry = PROJECT_SORTS.find((item) => item.field === field);
    if (!entry || (entry.completedOnly && !isCompleted)) return 'number';
    return sort;
};

const compareNumbers = (a, b) => {
    const aNumber = projectNumberValue(a);
    const bNumber = projectNumberValue(b);
    return aNumber === bNumber ? 0 : aNumber - bNumber;
};

const sortProjects = (projects, sort) => {
    const sorted = [...projects];
    const [field, direction] = sort.split('-');
    const sign = direction === 'desc' ? -1 : 1;
    if (field === 'name') {
        return sorted.sort((a, b) => sign * (a?.name || '').localeCompare(b?.name || '', undefined, { sensitivity: 'base' }) || compareNumbers(a, b));
    }
    if (SORT_DATE_FIELDS[field]) {
        const dateOf = (project) => (project?.[SORT_DATE_FIELDS[field]] || '').slice(0, 10);
        return sorted.sort((a, b) => {
            const aDate = dateOf(a);
            const bDate = dateOf(b);
            if (aDate === bDate) return compareNumbers(a, b);
            if (!aDate) return 1;
            if (!bDate) return -1;
            return sign * aDate.localeCompare(bDate);
        });
    }
    return sorted.sort((a, b) => sign * compareNumbers(a, b));
};

const TabPanel = ({ children, value, index }) => {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`tabpanel-${index}`}
            aria-labelledby={`tab-${index}`}
        >
            {value === index && (
                <PanelBox aria-label='PanelBox'>
                    {children}
                </PanelBox>
            )}
        </div>
    );
};

function LinearProgressWithLabel(props, value) {
    return (
        <PercentageBox>
            <PercentageBar>
                <LinearProgress variant="determinate" {...props} />
            </PercentageBar>
            <PercentageText>
                <Typography variant="body2" color="text.secondary">{`${Math.round(props.value)}%`}</Typography>
            </PercentageText>
        </PercentageBox>
    );
}

const AccountTabs = (data) => {
    const { accountId, accountData, accountProjects, accountGroups, fetchProjects, loading } = data;
    const isLocked = useIsLocked(accountData, accountData?.client);
    const navigate = useNavigate();
    const { accountSlug, tab } = useParams();
    const dispatch = useDispatch();
    
    // Get tab from URL params, default to 'projects'
    const currentTab = tab || 'projects';
    const [openAddProjectDialog, setOpenAddProjectDialog] = useState(false);
    const [openAddGroupDialog, setOpenAddGroupDialog] = useState(false);
    const [selectedGroupId, setSelectedGroupId] = useState("");
    const [editMode, setEditMode] = useState(false);
    const [selectedGroupData, setSelectedGroupData] = useState(null);
    const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
    const [deletionImpact, setDeletionImpact] = useState(null);
    const [groupToDelete, setGroupToDelete] = useState(null);
    const [groupToComplete, setGroupToComplete] = useState(null);
    const [tabValue, setTabValue] = useState(currentTab);
    const [currentStatus, setCurrentStatus] = useState(currentTab === 'completedProjects' ? 'completed' : 'open');
    const [expandedGroup, setExpandedGroup] = useState(null);
    const [groupVisibleCount, setGroupVisibleCount] = useState({});
    const [debouncedFilter, setDebouncedFilter] = useState('');
    const filterTimerRef = useRef(null);
    const filterInputRef = useRef(null);
    const isMobile = useMediaQuery('(max-width:899px)');
    const hasPermission = useHasPermission();
    const canCreateProject = hasPermission("addProjects") || hasPermission("addProjectButton");
    const canDeleteGroup = hasPermission("deleteGroups") || hasPermission("addProjectButton");
    const canEditGroup = hasPermission("editGroups");
    const canSeeInternal = hasPermission("canSeeInternal");
    const currentUser = useSelector((state) => state.auth.currentUser);
    const userId = useSelector((state) => state.auth.userId);
    const [view, setView] = useState(() => currentUser?.preferences?.UI?.projectsView || 'card');
    const [sort, setSort] = useState(() => currentUser?.preferences?.UI?.projectsSort || 'number');
    const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
    const [accountTasks, setAccountTasks] = useState([]);

    // Update tab value when URL changes (browser back/forward)
    useEffect(() => {
        setTabValue(currentTab);
    }, [currentTab]);

    const calendarItems = useMemo(() => {
        const projects = (accountProjects || []).map((p) => ({ ...p, _kind: 'Project' }));
        const tasks = (accountTasks || []).map((t) => ({
            _id: t._id,
            name: t.name,
            startDate: t.startDate,
            dueDate: t.dueDate,
            status: t.status,
            account: t.account,
            responsible: t.responsible,
            project: t.project,
            _kind: 'Task',
        }));
        return [...projects, ...tasks];
    }, [accountProjects, accountTasks]);

    const getCalendarLink = (item) => {
        const slug = item?.account?.slug || item?.account?.name;
        if (!slug) return null;
        if (item._kind === 'Task') {
            return item.project?._id ? `/account/${slug}/project/${item.project._id}` : null;
        }
        return `/account/${slug}/project/${item._id}`;
    };



    const handleViewChange = (newView) => {
        setView(newView);
        if (!userId) return;
        const preferences = {
            ...(currentUser?.preferences || {}),
            UI: {
                ...(currentUser?.preferences?.UI || {}),
                projectsView: newView,
            },
        };
        dispatch(setUserPreferences({ preferences }));
        dispatch(updateUser({ userId, userData: { preferences }, silent: true }));
    };

    const handleSortChange = (newSort) => {
        setSort(newSort);
        if (!userId) return;
        const preferences = {
            ...(currentUser?.preferences || {}),
            UI: {
                ...(currentUser?.preferences?.UI || {}),
                projectsSort: newSort,
            },
        };
        dispatch(setUserPreferences({ preferences }));
        dispatch(updateUser({ userId, userData: { preferences }, silent: true }));
    };

    const handleChange = (event, newValue) => {
        // Update URL without reloading the page
        navigate(`/account/${accountSlug}/${newValue}`, { replace: true });

        const newStatus = newValue === 'completedProjects' ? 'completed' : 'open';
        if (newStatus !== currentStatus) {
            setCurrentStatus(newStatus);
            fetchProjects(newStatus);
        }
        setExpandedGroup(null);
        setGroupVisibleCount({});
        setDebouncedFilter('');
        clearTimeout(filterTimerRef.current);
        if (filterInputRef.current) filterInputRef.current.value = '';
    };

    const handleOpenAddProjectDialog = (groupId = null) => {
        setSelectedGroupId(groupId);
        setOpenAddProjectDialog(true);
    };

    const handleCloseAddProjectDialog = () => {
        setSelectedGroupId(null);
        setOpenAddProjectDialog(false);
    };

    const handleOpenAddGroupDialog = (groupId = null) => {
        setSelectedGroupId(groupId);
        setEditMode(false);
        setSelectedGroupData(null);
        setOpenAddGroupDialog(true);
    };

    const handleCloseAddGroupDialog = () => {
        setSelectedGroupId(null);
        setEditMode(false);
        setSelectedGroupData(null);
        setOpenAddGroupDialog(false);
    };


    const handleDeleteGroup = (groupId) => {
        if (groupId) {
            setGroupToDelete(groupId);
            setConfirmDeleteDialogOpen(true);
        }
    };

    const confirmDeleteGroup = () => {
        if (groupToDelete) {
            dispatch(
                deleteGroup(groupToDelete, (response) => {
                    setConfirmDeleteDialogOpen(false);
                    setGroupToDelete(null);
                    if (response && response.group) {
                        dispatch(triggerRefresh({ triggerName: "groups" }));
                        fetchProjects(currentStatus);
                        if (response.billingImpactSummary) {
                            setDeletionImpact({ impact: response.billingImpact, summary: response.billingImpactSummary });
                        } else {
                            dispatch(showAlert({ severity: "success", message: "Group deleted successfully" }));
                        }
                    }
                })
            );
        }
    };

    const handleEditGroup = (groupId) => {
        const groupData = accountGroups.find(group => group._id === groupId);
        if (groupData) {
            setSelectedGroupData(groupData);
            setEditMode(true);
            setOpenAddGroupDialog(true);
        }
    };

    const GroupMenu = ({ groupId, canEditGroup, canDeleteGroup, setSelectedGroupId }) => {
        const [anchorEl, setAnchorEl] = useState(null);

        const handleMenuClick = (event) => {
            setAnchorEl(event.currentTarget);
            setSelectedGroupId(groupId);
        };

        const handleMenuClose = () => {
            setAnchorEl(null);
            setSelectedGroupId(null);
        };

        const handleEditClick = () => {
            handleEditGroup(groupId);
            setSelectedGroupId(groupId);
            handleMenuClose();
        };

        const handleDeleteClick = () => {
            handleDeleteGroup(groupId);
            setSelectedGroupId(groupId);
            handleMenuClose();
        };

        return (
            <>
                <IconButton size="small" onClick={handleMenuClick}>
                    <MoreVertIcon fontSize="small" />
                </IconButton>
                <Menu 
                    anchorEl={anchorEl} 
                    open={Boolean(anchorEl)} 
                    onClose={handleMenuClose}
                >
                    {canEditGroup && !isLocked && (
                        <MenuItem onClick={handleEditClick}>
                            <EditIcon fontSize="small" />
                            Edit Group
                        </MenuItem>
                    )}
                    {canEditGroup && !isLocked && (
                        <MenuItem onClick={() => { setGroupToComplete({ type: "group", id: groupId, name: accountGroups.find((g) => g._id === groupId)?.name }); handleMenuClose(); }}>
                            <DoneAllIcon fontSize="small" />
                            Complete Group
                        </MenuItem>
                    )}
                    {canDeleteGroup && !isLocked && (
                        <DeleteMenuItem onClick={handleDeleteClick}>
                            <DeleteIcon fontSize="small" />
                            Delete Group
                        </DeleteMenuItem>
                    )}
                </Menu>
            </>
        );
    };

    const LOAD_MORE_SIZE = 50;

    const ProjectsTabPanel = ({ projects, view, sort, status }) => {
        const isCompleted = status === 'completed';

        const groupedProjects = projects.reduce((acc, project) => {
            if (project.group) {
                if (!acc[project.group._id]) {
                    acc[project.group._id] = [];
                }
                acc[project.group._id].push(project);
            } else {
                if (!acc['ungrouped']) {
                    acc['ungrouped'] = [];
                }
                acc['ungrouped'].push(project);
            }
            return acc;
        }, {});

        const effectiveSort = effectiveSortFor(sort, isCompleted);
        Object.keys(groupedProjects).forEach((groupKey) => {
            groupedProjects[groupKey] = sortProjects(groupedProjects[groupKey], effectiveSort);
        });

        const sortedGroupKeys = Object.keys(groupedProjects).sort((a, b) => {
            if (a === 'ungrouped') return -1;
            if (b === 'ungrouped') return 1;
            return a.localeCompare(b);
        });

        const projectLabel = isCompleted ? 'Completed Project' : 'Open Project';

        const getGroupName = (groupKey) =>
            groupKey === 'ungrouped' ? 'Ungrouped' : (accountGroups.find(g => g._id === groupKey)?.name ?? '');

        const effectiveFilter = debouncedFilter.length >= 3 ? debouncedFilter.toLowerCase().trim() : '';

        const filteredGroupKeys = isCompleted
            ? sortedGroupKeys.filter((groupKey) => {
                if (!effectiveFilter) return true;
                if (getGroupName(groupKey).toLowerCase().includes(effectiveFilter)) return true;
                return groupedProjects[groupKey].some(p => p.name.toLowerCase().includes(effectiveFilter));
            })
            : sortedGroupKeys;

        const getFilteredProjects = (groupKey) => {
            if (!isCompleted || !effectiveFilter) return groupedProjects[groupKey];
            if (getGroupName(groupKey).toLowerCase().includes(effectiveFilter)) return groupedProjects[groupKey];
            return groupedProjects[groupKey].filter(p => p.name.toLowerCase().includes(effectiveFilter));
        };

        const totalFilteredProjects = effectiveFilter
            ? filteredGroupKeys.reduce((sum, k) => sum + getFilteredProjects(k).length, 0)
            : Infinity;
        const autoExpandAll = isCompleted && !!effectiveFilter && totalFilteredProjects < 100;

        const handleGroupToggle = (groupKey) => {
            if (expandedGroup === groupKey) {
                setExpandedGroup(null);
            } else {
                setExpandedGroup(groupKey);
                setGroupVisibleCount(prev => ({ ...prev, [groupKey]: LOAD_MORE_SIZE }));
            }
        };

        const handleLoadMore = (groupKey, e) => {
            e.stopPropagation();
            setGroupVisibleCount(prev => ({ ...prev, [groupKey]: (prev[groupKey] ?? LOAD_MORE_SIZE) + LOAD_MORE_SIZE }));
        };

        const renderProjects = (groupKey) => {
            const filteredProjects = getFilteredProjects(groupKey);
            const visibleCount = isCompleted ? (groupVisibleCount[groupKey] ?? LOAD_MORE_SIZE) : filteredProjects.length;
            const visibleProjects = isCompleted ? filteredProjects.slice(0, visibleCount) : filteredProjects;
            const remaining = filteredProjects.length - visibleProjects.length;

            return (
                <>
                    {(isMobile || view === "card") ? (
                        <GridContainer aria-label={`GridContainer-${groupKey}`}>
                            {visibleProjects.map((project, index) =>
                                <AccountTabProjects key={index} project={project} />
                            )}
                        </GridContainer>
                    ) : (
                        <ProjectsTable size="small" aria-label={`ListContainer-${groupKey}`}>
                            <colgroup>
                                <col width={40} />
                                <col />
                                {canSeeInternal && <NumericCol />}
                                <NumericCol />
                                <NumericCol />
                                <col width={160} />
                                <IconsCol />
                            </colgroup>
                            <TableBody>
                                {visibleProjects.map((project, index) =>
                                    <AccountTabProjectsList key={index} project={project} />
                                )}
                            </TableBody>
                        </ProjectsTable>
                    )}
                    {isCompleted && remaining > 0 && (
                        <LoadMoreBox>
                            <Button size="small" onClick={(e) => handleLoadMore(groupKey, e)}>
                                Load More ({remaining} remaining)
                            </Button>
                        </LoadMoreBox>
                    )}
                </>
            );
        };

        return (
            <div>
                {filteredGroupKeys.map((group) => {
                    const isUngrouped = group === 'ungrouped';
                    const isOpen = isUngrouped || !isCompleted || autoExpandAll || expandedGroup === group;

                    return (
                        <div key={group}>
                            <StyledGroupHeader
                                onClick={isCompleted && !isUngrouped ? () => handleGroupToggle(group) : undefined}
                                clickable={isCompleted && !isUngrouped}
                            >
                                <HeaderTitleBox>
                                    {isCompleted && !isUngrouped && (
                                        <GroupExpandIcon expanded={expandedGroup === group} />
                                    )}
                                    <GroupTitle variant="h6">{getGroupName(group)}</GroupTitle>
                                    {groupedProjects[group].length > 0 && (
                                        <ProjectCount label={`${groupedProjects[group].length} ${projectLabel}${groupedProjects[group].length !== 1 ? "s" : ""}`} size="small" />
                                    )}
                                    {!isUngrouped && <Chip label={`${Math.round(accountGroups.find((g) => g._id === group)?.completion) || "0"}% Group Completion`} size="small" />}
                                    <Chip color="primary" label={`${groupedProjects[group].reduce((sum, p) => sum + addUpTime(convertMinutesToHours(p.time?.total)), 0).toFixed(2)} hrs`} size="small" variant="outlined" />
                                </HeaderTitleBox>

                                {!isUngrouped && !isCompleted && !isLocked && (
                                    <HeaderActionsBox>
                                        <AddProjectToGroupButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenAddProjectDialog(group); }} title="Add project to this group">
                                            <AddIcon fontSize="small" />
                                        </AddProjectToGroupButton>

                                        <GroupMenu groupId={group} canEditGroup={canEditGroup} canDeleteGroup={canDeleteGroup} setSelectedGroupId={setSelectedGroupId} />
                                    </HeaderActionsBox>
                                )}
                            </StyledGroupHeader>
                            {!isUngrouped ? <LinearProgressWithLabel value={Math.round(accountGroups.find((g) => g._id === group)?.completion || 0)} /> : <UngroupedDivider />}

                            {isOpen && renderProjects(group)}
                        </div>
                    );
                })}
            </div>
        );
    };

    const ViewSelector = ({ handleViewChange, view, handleSortChange, sort, isCompleted }) => {
        const [sortAnchorEl, setSortAnchorEl] = useState(null);
        const effectiveSort = effectiveSortFor(sort, isCompleted);

        const handleSortMenuOpen = (event) => setSortAnchorEl(event.currentTarget);
        const handleSortMenuClose = () => setSortAnchorEl(null);

        const [activeField, activeDirection] = effectiveSort.split('-');

        const handleSortSelect = (entry) => {
            if (entry.field === activeField) {
                handleSortChange(sortKeyOf(entry.field, activeDirection === 'desc' ? 'asc' : 'desc'));
                return;
            }
            handleSortChange(sortKeyOf(entry.field, entry.defaultDirection));
        };

        return (
            <>
                < ViewBox >
                    <Tooltip title="Sort Projects">
                        <IconButton
                            color={effectiveSort !== 'number' ? 'primary' : 'default'}
                            onClick={handleSortMenuOpen}
                            aria-label="Sort projects"
                        >
                            <SortIcon />
                        </IconButton>
                    </Tooltip>
                    <Menu anchorEl={sortAnchorEl} open={Boolean(sortAnchorEl)} onClose={handleSortMenuClose}>
                        {PROJECT_SORTS.filter((entry) => !entry.completedOnly || isCompleted).map((entry) => {
                            const active = entry.field === activeField;
                            return (
                                <SortMenuItem key={entry.field} selected={active} onClick={() => handleSortSelect(entry)}>
                                    {entry.label}
                                    <SortDirectionIcon>
                                        {active && (activeDirection === 'desc' ? <ArrowDownwardIcon fontSize="small" /> : <ArrowUpwardIcon fontSize="small" />)}
                                    </SortDirectionIcon>
                                </SortMenuItem>
                            );
                        })}
                    </Menu>
                    <Tooltip title="Card View">
                        <IconButton
                            color={view === 'card' ? 'primary' : 'default'}
                            onClick={() => handleViewChange('card')}
                        >
                            <ViewModuleIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="List View">
                        <ListViewToggle
                            color={view === 'list' ? 'primary' : 'default'}
                            onClick={() => handleViewChange('list')}
                        >
                            <ViewListIcon />
                        </ListViewToggle>
                    </Tooltip>
                </ViewBox >
                <Divider />
            </>
        )
    }

    return (
        <>
            <TabContainer>
                <StyledTabs textColor="primary" indicatorColor="primary" variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile value={tabValue} onChange={handleChange}>
                    <Tab value="projects" label="Projects" />
                    <Tab value="calendar" label="Calendar" />
                    <Tab value="completedProjects" label="Completed" />
                    <Tab value="activity" label="Activity" />
                    <Tab value="people" label="People" />
                </StyledTabs>

                {canCreateProject && (
                    <AddButtons>
                        {!canSeeInternal && (
                        <RequestQuoteButton variant="outlined" color="primary" size="small" onClick={() => setQuoteDialogOpen(true)} disabled={isLocked}>
                            <RequestQuoteIcon fontSize="16px" /> Request a Quote
                        </RequestQuoteButton>
                        )}
                        <AddGroupButton variant="outlined" color="primary" size="small" onClick={handleOpenAddGroupDialog} disabled={isLocked}>
                            <Add fontSize="16px" /> Add Group
                        </AddGroupButton>

                        <AddProjectButton variant="contained" color="primary" size="small" onClick={() => handleOpenAddProjectDialog(null)} disabled={isLocked}>
                            <Add fontSize="16px" /> Add Project
                        </AddProjectButton>
                    </AddButtons>
                )}
            </TabContainer>

            <TabPanel value={tabValue} index="projects">
                <ViewSelector handleViewChange={handleViewChange} view={view} handleSortChange={handleSortChange} sort={sort} isCompleted={false} />
                {loading ? <CircularProgress /> : ProjectsTabPanel({ projects: accountProjects, view, sort, status: currentStatus })}
            </TabPanel>

            <TabPanel value={tabValue} index="calendar">
                {loading ? <CircularProgress /> : <AccountTabCalendar projects={calendarItems} getLink={getCalendarLink} />}
            </TabPanel>

            <TabPanel value={tabValue} index="completedProjects">
                <ViewSelector handleViewChange={handleViewChange} view={view} handleSortChange={handleSortChange} sort={sort} isCompleted />
                <FilterBox>
                    <TextField
                        size="small"
                        placeholder="Filter groups and projects..."
                        defaultValue=""
                        inputRef={filterInputRef}
                        onChange={(e) => {
                            const val = e.target.value;
                            clearTimeout(filterTimerRef.current);
                            filterTimerRef.current = setTimeout(() => setDebouncedFilter(val), 300);
                        }}
                        fullWidth
                    />
                </FilterBox>
                {loading ? <CircularProgress /> : ProjectsTabPanel({ projects: accountProjects, view, sort, status: currentStatus })}
            </TabPanel>

            <TabPanel value={tabValue} index="activity">
                {loading ? <CircularProgress /> : <AccountTabActivity accountId={accountId} accountSlug={accountSlug} />}
            </TabPanel>

            <TabPanel value={tabValue} index="people">
                {loading ? <CircularProgress /> : <AccountTabPeople accountId={accountId} accountData={accountData} />}
            </TabPanel>

            <CreateProjectDialog open={openAddProjectDialog} onClose={handleCloseAddProjectDialog} accountId={accountId} accountData={accountData} accountGroups={accountGroups} groupId={selectedGroupId} />
            <CreateGroupDialog
                open={openAddGroupDialog}
                onClose={handleCloseAddGroupDialog}
                accountId={accountId}
                accountData={accountData}
                groupId={selectedGroupId}
                editMode={editMode}
                groupData={selectedGroupData}
            />

            <Dialog open={confirmDeleteDialogOpen} onClose={() => setConfirmDeleteDialogOpen(false)}>
                <DialogTitle>Delete Group</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete the group "{accountGroups.find((g) => g._id === groupToDelete)?.name}"?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={confirmDeleteGroup} variant="contained" color="error">Delete</Button>
                </DialogActions>
            </Dialog>

            <BillingImpactDialog
                open={!!deletionImpact}
                impact={deletionImpact?.impact}
                summary={deletionImpact?.summary}
                onClose={() => setDeletionImpact(null)}
            />

            <CompleteCascadeDialog
                target={groupToComplete}
                onClose={() => setGroupToComplete(null)}
                onCompleted={() => fetchProjects(currentStatus)}
            />

            <QuoteRequestDialog
                open={quoteDialogOpen}
                onClose={() => setQuoteDialogOpen(false)}
                accountId={accountId}
            />
        </>
    );
};

export default AccountTabs;